import { useReactiveVar } from "@apollo/client";
import { mdiRestore } from "@mdi/js";
import Icon from "@mdi/react";
import { BetQLService } from "src/common-services";
import React, { useState } from "react";
import ButtonGroup from "src/components/ButtonGroup";
import Dropdown from "src/components/Dropdown";
import AnalyticsService from "src/services/AnalyticsService";
import { currentSport } from "src/services/ApolloService";
import ScheduleTable from "./ScheduleTable";
import styles from "./styles.scss";

const { getTeamRecord } = BetQLService;

const LAST_X_GAMES_FILTER = [
  {
    name: "All Games",
    key: null,
  },
  {
    name: "Last 5 Games",
    key: 5,
  },
  {
    name: "Last 10 Games",
    key: 10,
  },
  {
    name: "Last 30 Games",
    key: 30,
  },
  {
    name: "Last 60 Games",
    key: 60,
  },
] as const;

const ScheduleRecordInfo = ({ schedule, team }: { schedule: any; team: any }) => {
  let wins = 0;
  let losses = 0;
  let draws = 0;

  let atswins = 0;
  let atslosses = 0;
  let atsties = 0;

  let ouwins = 0;
  let oulosses = 0;
  let outies = 0;

  let numOfPlayedGames = 0;

  // Record for the last X games
  schedule?.forEach((event: any) => {
    if (!event.line) {
      return;
    }

    const isHome = event.homeTeam.id === team.id;

    // Record
    const { homeScore, awayScore } = event;
    if (homeScore !== null || awayScore !== null) {
      const teamScore = isHome ? homeScore : awayScore;
      const oppScore = isHome ? awayScore : homeScore;

      const isTeamWin = teamScore > oppScore;
      const isTeamLoss = teamScore < oppScore;
      const isTeamDraw = teamScore === oppScore;

      isTeamWin && wins++;
      isTeamLoss && losses++;
      isTeamDraw && draws++;

      // ATS Record
      const atsWin = event.line[`${isHome ? "home" : "away"}AtsWin`];
      const spread: number = event?.line?.[`${isHome ? "home" : "away"}Spread`];

      // Win++
      // 1. Spread 0 == Even, did the home team win?
      // 2. AtsWin = true
      if ((spread === 0 && isTeamWin && atsWin === null) || (atsWin !== null && spread && atsWin)) {
        atswins++;
      }

      // Loss++
      // 1. Spread 0 == Even, did the home team lose?
      // 2. AtsWin = false
      if ((spread === 0 && isTeamLoss && atsWin === null) || (atsWin !== null && spread && !atsWin)) {
        atslosses++;
      }

      // Push++
      // 1. Spread 0 == Even, did the home team, away team tied?
      // 2. AtsWin = null with spread
      if ((spread === 0 && isTeamDraw && atsWin === null) || (spread && atsWin === null)) {
        atsties++;
      }

      // OU Record
      const lineOu: boolean = event.line.ouWin;
      lineOu === null ? outies++ : lineOu ? ouwins++ : oulosses++;

      // Get how many game is finished
      numOfPlayedGames++;
    }
  });

  const currentSportObj = useReactiveVar(currentSport) ?? null;
  const record = getTeamRecord(
    {
      teamStats: {
        wins,
        losses: currentSportObj?.key === "NHL" && team.overtimeLosses !== null ? losses - team.overtimeLosses : losses,
        suties: draws,
        overtimeLosses: team.overtimeLosses,
      },
    },
    currentSportObj
  );

  const atsRecord = `${atswins}-${atslosses}-${atsties}`;

  const ouRecord = `${ouwins}-${oulosses}-${outies}`;

  return (
    <div className="schedule-list__info_item_container">
      {/** For desktops and tablets */}
      <div className="schedule-list__info_item_container-long">
        <span>Total Games: {numOfPlayedGames}</span>
        <span>Record: {record} </span>
        <span>ATS Record: {atsRecord}</span>
        <span>O/U Record: {ouRecord}</span>
      </div>
      {/** Shorter text for mobile devices */}
      <div className="schedule-list__info_item_container-short">
        <span>Games: {numOfPlayedGames}</span>
        <span>Record: {record} </span>
        <span>ATS Record: {atsRecord}</span>
        <span>O/U Record: {ouRecord}</span>
      </div>
    </div>
  );
};

const ScheduleList = ({ event }: { event: any }) => {
  const league = useReactiveVar(currentSport);
  const [side, setSide] = useState<"home" | "away">("home");
  const [lastXGames, setLastXGames] = useState<number | null>(null);
  const [homeAwayFilter, setHomeAwayFilter] = useState<"home" | "away" | null>(null); // prettier-ignore
  const [dogFavFilter, setDogFavFilter] = useState<"dog" | "fav" | null>(null);
  const TEAM_OPTIONS = [
    {
      name: league?.isCollege ? event.awayTeam.fullName : event.awayTeam.lastName, // prettier-ignore
      key: "away",
    },
    {
      name: league?.isCollege ? event.homeTeam.fullName : event.homeTeam.lastName, // prettier-ignore
      key: "home",
    },
  ] as const;

  const handleChangeLastXGames = (value: number | null) => {
    setLastXGames(value);

    AnalyticsService.track("Change Last Games", {
      screen: "GameView",
      sport: league?.name,
      awayTeam: league?.isCollege ? event.awayTeam.fullName : event.awayTeam.lastName, // prettier-ignore
      homeTeam: league?.isCollege ? event.homeTeam.fullName : event.homeTeam.lastName, // prettier-ignore
      numGames: value,
    });
  };

  const handleChangeSide = (_side: "home" | "away") => {
    setSide(_side);

    AnalyticsService.track("Change Team", {
      screen: "GameView",
      sport: league?.name,
      awayTeam: league?.isCollege ? event.awayTeam.fullName : event.awayTeam.lastName, // prettier-ignore
      homeTeam: league?.isCollege ? event.homeTeam.fullName : event.homeTeam.lastName, // prettier-ignore
    });
  };

  /**
   * Filter by home/away
   */
  const AWAY_HOME_FILTER = [
    {
      name: "Home",
      key: "home",
    },
    {
      name: "Away",
      key: "away",
    },
  ] as const;

  /**
   * Entry to filter by under/favorite
   */
  const DOG_FAV_FILTER = [
    {
      name: "Favorite",
      key: "fav",
    },
    {
      name: "Underdog",
      key: "dog",
    },
  ] as const;

  const team = side === "home" ? event.homeTeam : event.awayTeam;

  let filteredSchedule = team?.schedule || [];

  // Hide preseason games
  filteredSchedule = filteredSchedule.filter((game: any) => game.matchup?.seasonType !== "PRESEASON");

  // Filter by home/away games
  if (homeAwayFilter) {
    filteredSchedule = filteredSchedule.filter(
      (game: any) => game[`${homeAwayFilter}Team`]?.id === event[`${side}Team`].id
    );
  }

  // Filter by favorite/underdog games
  if (dogFavFilter === "dog") {
    filteredSchedule = filteredSchedule.filter((game: any) => {
      const dogFavFilterOnCurrentTeam = homeAwayFilter
        ? homeAwayFilter
        : team.id === game.awayTeam.id
        ? "away"
        : "home";

      return !game.line || game.line[`${dogFavFilterOnCurrentTeam}Spread`] > 0;
    });
  }
  if (dogFavFilter === "fav") {
    filteredSchedule = filteredSchedule.filter((game: any) => {
      const dogFavFilterOnCurrentTeam = homeAwayFilter
        ? homeAwayFilter
        : team.id === game.awayTeam.id
        ? "away"
        : "home";

      return !game.line || game.line[`${dogFavFilterOnCurrentTeam}Spread`] < 0;
    });
  }
  // Last X games filter
  if (lastXGames) {
    filteredSchedule = filteredSchedule.filter((game: any) => game.homeScore || game.awayScore);
    filteredSchedule = filteredSchedule.slice(-lastXGames);
  }
  const teamButtonGroup = (
    <React.Fragment>
      <div className="schedule-list__filter-section-md schedule-list__filter-section-lg">
        <ButtonGroup
          equalWidth={true}
          className="schedule-list__filter-section-button-group"
          onSelectItem={(item) => handleChangeSide(item.key as any)}
          selectedIndex={TEAM_OPTIONS.map((x) => x.key).indexOf(side)}
          items={TEAM_OPTIONS}
        />
      </div>
      <div className="schedule-list__filter-section-xs">
        <ButtonGroup
          equalWidth={true}
          className="schedule-list__filter-section-button-group"
          onSelectItem={(item) => handleChangeSide(item.key as any)}
          selectedIndex={TEAM_OPTIONS.map((x) => x.key).indexOf(side)}
          items={TEAM_OPTIONS}
        />
      </div>
    </React.Fragment>
  );

  const resetButton = (
    <div className="schedule-list__filter-section-control">
      <span
        className="schedule-list__filter-section-text-button"
        onClick={() => {
          setHomeAwayFilter(null);
          setDogFavFilter(null);
        }}
      >
        <span>Reset Filters </span>
        <span className="schedule-list__filter-section-text-button-icon">
          <Icon path={mdiRestore} size="17px" color={"#004198"} />
        </span>
      </span>
    </div>
  );

  const displayB2BColumn = league?.sport.name !== "Soccer" && league?.sport.name !== "Football";
  const displayWeekColumn = league?.name === "NFL";
  const numberOFColumns = () => {
    if (displayB2BColumn || displayWeekColumn) {
      return "columns-6";
    }
    return "columns-5";
  };

  const hasFilter = homeAwayFilter !== null || dogFavFilter !== null;

  return (
    <div className="schedule-list__container">
      <div className="schedule-list__title-container">
        <div className="schedule-list__title">Schedule &amp; Result</div>
      </div>
      <div className="schedule-list__header-filters">
        {teamButtonGroup}
        <div className="schedule-list__filter-section-xs">{resetButton}</div>
        <div className="schedule-list__filter-section">
          <div className="schedule-list__filter-section-lg">{resetButton}</div>

          <div className="schedule-list__filter-section-md schedule-list__filter-section-lg">
            <div className="schedule-list__inline-button-groups">
              <ButtonGroup
                equalWidth={true}
                className="schedule-list__filter-section-button-group"
                onSelectItem={(x) => setHomeAwayFilter(homeAwayFilter === x.key ? null : x.key as any)} // prettier-ignore
                selectedIndex={AWAY_HOME_FILTER.map((x) => x.key).indexOf(homeAwayFilter as any)} // prettier-ignore
                items={AWAY_HOME_FILTER}
              />
              <ButtonGroup
                equalWidth={true}
                className="schedule-list__filter-section-button-group"
                onSelectItem={(x) => setDogFavFilter(dogFavFilter === x.key ? null : x.key as any)} // prettier-ignore
                selectedIndex={DOG_FAV_FILTER.map((x) => x.key).indexOf(dogFavFilter as any)} // prettier-ignore
                items={DOG_FAV_FILTER}
              />
            </div>
          </div>
          <div className="schedule-list__filter-section-xs">
            <ButtonGroup
              equalWidth={true}
              className="schedule-list__filter-section-button-group"
              onSelectItem={(x) => setHomeAwayFilter(homeAwayFilter === x.key ? null : x.key as any)} // prettier-ignore
              selectedIndex={AWAY_HOME_FILTER.map((x) => x.key).indexOf(homeAwayFilter as any)} // prettier-ignore
              items={AWAY_HOME_FILTER}
            />
            <ButtonGroup
              equalWidth={true}
              className="schedule-list__filter-section-button-group"
              onSelectItem={(x) => setDogFavFilter(dogFavFilter === x.key ? null : x.key as any)} // prettier-ignore
              selectedIndex={DOG_FAV_FILTER.map((x) => x.key).indexOf(dogFavFilter as any)} // prettier-ignore
              items={DOG_FAV_FILTER}
            />
          </div>
        </div>
      </div>

      {/* Last X games filter and stats for games shown */}
      <div className="schedule-list__header">
        {league?.sport.name !== "Football" && (
          <Dropdown
            items={LAST_X_GAMES_FILTER as any}
            selectedIndex={LAST_X_GAMES_FILTER.map((x) => x.key).indexOf(lastXGames as any)} // prettier-ignore
            onSelectItem={(x) => handleChangeLastXGames(x.key as any)}
          />
        )}
        <ScheduleRecordInfo schedule={filteredSchedule} team={team} />
      </div>

      <div style={{ overflowX: "auto" }}>
        <div className="schedule-list__overflow-right-list">
          <div className={numberOFColumns()}>
            {displayWeekColumn ? <span className="schedule-list__table-column">Week</span> : null}
            <span className="schedule-list__table-column">Date</span>
            <span className="schedule-list__table-column">Opponent</span>
            <span className="schedule-list__table-column">Score</span>
            <span className="schedule-list__table-column">ATS</span>
            <span className="schedule-list__table-column schedule-list__table-item-5">O/U</span>
            {displayB2BColumn && <span className="schedule-list__table-column schedule-list__table-item-6">B2B</span>}
          </div>
        </div>

        <ScheduleTable
          filteredSchedule={filteredSchedule}
          freeGame={event.freeGame}
          team={team}
          hasFilter={hasFilter}
        />
      </div>
      <style jsx global>
        {styles}
      </style>
    </div>
  );
};

export default ScheduleList;
